import React, { useEffect, useMemo, useRef, useState } from "react";
import "./orderHistory.scss";

export default function OrderHistory({
  userOrders,
  stackedOrderIds,
  selectedOrderId,
  onOrderOpen,
  onOrderClose,
}) {
  const COMPACT_CONTROLS_BREAKPOINT_PX = 980;
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [imgDims, setImgDims] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortOption, setSortOption] = useState("desc"); // 'desc' | 'asc'
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [compactControls, setCompactControls] = useState(false);

  const searchRowRef = useRef(null);
  const searchInputRef = useRef(null);
  const advancedControlsRef = useRef(null);
  const requiredControlsWidthRef = useRef(null);

  const toDateSafe = (ts) => {
    if (!ts) return null;
    if (ts instanceof Date) return ts;
    if (typeof ts?.toDate === "function") {
      try {
        return ts.toDate();
      } catch (_) {
        return null;
      }
    }
    if (
      typeof ts === "object" &&
      ts !== null &&
      typeof ts.seconds === "number"
    ) {
      return new Date(ts.seconds * 1000);
    }
    if (typeof ts === "number") {
      const ms = ts < 1e12 ? ts * 1000 : ts;
      return new Date(ms);
    }
    if (typeof ts === "string") {
      const d = new Date(ts);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const formatDateTime = (val) => {
    const d = toDateSafe(val);
    if (!d) return "Unknown date";
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const formatMoney = (val) => {
    const n = typeof val === "string" ? Number(val) : Number(val ?? 0);
    const safe = Number.isFinite(n) ? n : 0;
    return safe.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
    });
  };

  const toMoneyNumber = (val) => {
    if (val === null || val === undefined || val === "") return null;
    const n = typeof val === "string" ? Number(val) : Number(val);
    return Number.isFinite(n) ? n : null;
  };

  const normalizePicture = (pic, index) => {
    if (!pic)
      return {
        url: "",
        name: `Image ${index + 1}`,
        quantity: 1,
        widthIn: null,
        heightIn: null,
      };
    if (typeof pic === "string") {
      const last = pic.split("/").pop() || "";
      const name =
        decodeURIComponent(last.split("?")[0] || "") || `Image ${index + 1}`;
      return { url: pic, name, quantity: 1, widthIn: null, heightIn: null };
    }
    const url = pic.file || pic.url || pic.downloadUrl || pic.src || "";
    const name =
      pic.name ||
      (url
        ? decodeURIComponent((url.split("/").pop() || "").split("?")[0] || "")
        : "") ||
      `Image ${index + 1}`;
    const quantity = Number(pic.quantity ?? pic.count ?? 1);
    const widthIn = Number(pic.widthIn);
    const heightIn = Number(pic.heightIn);
    return {
      url,
      name,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      widthIn: Number.isFinite(widthIn) ? widthIn : null,
      heightIn: Number.isFinite(heightIn) ? heightIn : null,
    };
  };

  const getOrderPictures = (order) => {
    if (Array.isArray(order?.pictures) && order.pictures.length)
      return order.pictures;
    if (Array.isArray(order?.images) && order.images.length)
      return order.images;
    return [];
  };

  const sumQuantities = (items) => {
    if (!Array.isArray(items) || !items.length) return 0;
    return items.reduce((sum, it) => {
      if (typeof it === "string") return sum + 1;
      const q = Number(it?.quantity ?? it?.count ?? 1);
      return sum + (Number.isFinite(q) && q > 0 ? q : 1);
    }, 0);
  };

  const statusKey = (status) =>
    String(status || "Pending")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setSortOption("desc");
    setPageIndex(0);
  };

  useEffect(() => {
    if (!filtersOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setFiltersOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [filtersOpen]);

  const computeRequiredControlsWidth = () => {
    const inputEl = searchInputRef.current;
    const advancedEl = advancedControlsRef.current;
    if (!inputEl || !advancedEl) return null;

    const inputWidth = inputEl.getBoundingClientRect().width;
    const advancedChildren = Array.from(advancedEl.children || []);
    if (!advancedChildren.length) return null;

    const advancedGapRaw =
      getComputedStyle(advancedEl).columnGap ||
      getComputedStyle(advancedEl).gap ||
      "0px";
    const advancedGap = Number.parseFloat(advancedGapRaw) || 0;

    const advancedRequiredWidth =
      advancedChildren.reduce(
        (sum, child) => sum + child.getBoundingClientRect().width,
        0
      ) +
      advancedGap * (advancedChildren.length - 1);

    const rowGapRaw =
      getComputedStyle(searchRowRef.current).columnGap ||
      getComputedStyle(searchRowRef.current).gap ||
      "0px";
    const rowGap = Number.parseFloat(rowGapRaw) || 0;

    return inputWidth + rowGap + advancedRequiredWidth;
  };

  useEffect(() => {
    const container = searchRowRef.current;
    if (!container) return;

    const update = () => {
      const belowBreakpoint =
        typeof window !== "undefined" &&
        Number.isFinite(window.innerWidth) &&
        window.innerWidth <= COMPACT_CONTROLS_BREAKPOINT_PX;

      if (belowBreakpoint) {
        setCompactControls(true);
        return;
      }

      if (!requiredControlsWidthRef.current && !compactControls) {
        requiredControlsWidthRef.current = computeRequiredControlsWidth();
      }
      const required = requiredControlsWidthRef.current;
      if (!required) return;
      const w = container.getBoundingClientRect().width;
      setCompactControls(w < required - 1);
    };

    const ro = new ResizeObserver(() => update());
    ro.observe(container);

    const raf = requestAnimationFrame(() => update());
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [compactControls]);

  useEffect(() => {
    if (compactControls) return;
    const raf = requestAnimationFrame(() => {
      const next = computeRequiredControlsWidth();
      if (!next) return;
      requiredControlsWidthRef.current = next;
    });
    return () => cancelAnimationFrame(raf);
  }, [compactControls]);

  useEffect(() => {
    if (!selectedOrder) return;
    const pics = getOrderPictures(selectedOrder);
    pics.forEach((pic, index) => {
      const normalized = normalizePicture(pic, index);
      const src = normalized.url;
      if (!src || imgDims[src]) return;
      const image = new Image();
      image.onload = () => {
        setImgDims((prev) => ({
          ...prev,
          [src]: { width: image.naturalWidth, height: image.naturalHeight },
        }));
      };
      image.src = src;
    });
  }, [selectedOrder]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (previewUrl) {
          setPreviewUrl("");
          setPreviewTitle("");
          return;
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewUrl]);

  const handleOpenDetails = (order) => {
    setSelectedOrder(order);
    if (onOrderOpen) onOrderOpen(order.id);
  };

  const handleCloseDetails = () => {
    setSelectedOrder(null);
    if (onOrderClose) onOrderClose();
  };

  // Open the detail panel if a matching selectedOrderId is present in URL
  useEffect(() => {
    if (!selectedOrderId) return;
    const order = (userOrders || []).find(
      (o) => String(o.id) === String(selectedOrderId)
    );
    if (order) setSelectedOrder(order);
  }, [selectedOrderId, userOrders]);

  const orders = Array.isArray(userOrders) ? userOrders : [];
  const filteredOrders = useMemo(() => {
    const term = String(searchTerm || "")
      .trim()
      .toLowerCase();
    const status = String(statusFilter || "").trim();
    const items = orders
      .filter((order) => {
        const id = String(order?.id || "");
        const matchesId = !term || id.toLowerCase().includes(term);
        const matchesStatus = !status || String(order?.status || "") === status;
        return matchesId && matchesStatus;
      })
      .sort((a, b) => {
        const aDate = toDateSafe(a?.createdAt) || new Date(0);
        const bDate = toDateSafe(b?.createdAt) || new Date(0);
        return sortOption === "desc" ? bDate - aDate : aDate - bDate;
      });
    return items;
  }, [orders, searchTerm, sortOption, statusFilter]);

  useEffect(() => {
    setPageIndex(0);
  }, [searchTerm, statusFilter, sortOption, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  const safePageIndex = Math.min(Math.max(0, pageIndex), totalPages - 1);
  const pagedOrders = useMemo(() => {
    const start = safePageIndex * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, pageSize, safePageIndex]);

  const selectedId = selectedOrder ? String(selectedOrder.id) : "";
  const invoiceCreated = useMemo(() => {
    if (!selectedOrder) return null;
    const hasInvoice =
      Boolean(selectedOrder?.invoice?.pdfUrl) ||
      Boolean(selectedOrder?.invoice?.invoiceId) ||
      Boolean(selectedOrder?.invoiceId);
    if (hasInvoice) return true;
    if (!Array.isArray(stackedOrderIds)) return null;
    return !stackedOrderIds.some(
      (id) => String(id) === String(selectedOrder.id)
    );
  }, [selectedOrder, stackedOrderIds]);

  const totalOrders = orders.length;
  const openOrders = orders.filter((o) => {
    const key = statusKey(o?.status);
    return (
      key && !["completed", "cancelled", "canceled", "refunded"].includes(key)
    );
  }).length;
  const completedOrders = orders.filter(
    (o) => statusKey(o?.status) === "completed"
  ).length;

  const statusFlow = [
    { key: "pending", label: "Pending" },
    { key: "downloaded", label: "Downloaded" },
    {
      key: "in-progress",
      label: "In Progress",
      aliases: ["processing", "inprogress"],
    },
    { key: "completed", label: "Completed", aliases: ["complete"] },
  ];

  const normalizedStatusKey = statusKey(selectedOrder?.status);
  const isCancelled = ["cancelled", "canceled", "refunded"].includes(
    normalizedStatusKey
  );
  const renderStatusFlow = isCancelled
    ? statusFlow.map((step, idx) =>
        idx === statusFlow.length - 1
          ? { ...step, key: "canceled", label: "Canceled" }
          : step
      )
    : statusFlow;
  const activeStatusIndex = (() => {
    if (isCancelled) return renderStatusFlow.length - 1;
    const idx = statusFlow.findIndex(
      (step) =>
        step.key === normalizedStatusKey ||
        (step.aliases || []).includes(normalizedStatusKey)
    );
    return idx === -1 ? 0 : idx;
  })();
  const statusProgressRatio =
    renderStatusFlow.length > 1
      ? Math.min(
          1,
          Math.max(0, activeStatusIndex / (renderStatusFlow.length - 1))
        )
      : 0;

  return (
    <div
      className={`cp-orders ${compactControls ? "is-compact-controls" : ""}`}
    >
      <div className="cp-orders__toolbar">
        <div className="cp-orders__toolbarTitle">
          <div className="cp-orders__sectionTitle">Find your orders</div>
          <div className="cp-orders__sectionSub">
            Search, filter, and open an order to view uploaded pictures and
            invoices.
          </div>
        </div>

        <div className="cp-orders__controls">
          <div className="cp-orders__searchRow" ref={searchRowRef}>
            <input
              className="cp-orders__input"
              type="text"
              placeholder="Search by order ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              ref={searchInputRef}
            />

            <div
              className="cp-orders__advancedControls"
              ref={advancedControlsRef}
            >
              <select
                className="cp-orders__select cp-orders__control--advanced"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Downloaded">Downloaded</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>

              <select
                className="cp-orders__select cp-orders__control--advanced"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>

              <button
                type="button"
                className="cp-orders__controlBtn cp-orders__control--advanced"
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>

            <button
              type="button"
              className="cp-orders__filtersIconBtn"
              onClick={() => setFiltersOpen(true)}
              aria-label="Filters"
              title="Filters"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
                <path
                  d="M3 6h18M6 12h12M10 18h4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {filtersOpen ? (
        <div
          className="cp-orders__filtersOverlay"
          role="dialog"
          aria-modal="true"
          aria-label="Order filters"
          onClick={() => setFiltersOpen(false)}
        >
          <div
            className="cp-orders__filtersSheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-orders__filtersHeader">
              <div className="cp-orders__filtersTitle">Filters</div>
              <button
                type="button"
                className="op-btn op-btn--ghost"
                onClick={() => setFiltersOpen(false)}
              >
                Done
              </button>
            </div>
            <div className="cp-orders__filtersBody">
              <select
                className="cp-orders__select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Downloaded">Downloaded</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>

              <select
                className="cp-orders__select"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>

              <button
                type="button"
                className="cp-orders__controlBtn"
                onClick={() => {
                  clearFilters();
                }}
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="cp-orders__layout">
        <div className="cp-orders__panel">
          <div className="cp-orders__panelHeader">
            <div className="cp-orders__panelTitle">Orders</div>
            <div className="cp-orders__panelHint">
              Click a row to view details
            </div>
          </div>

          <div className="cp-orders__list" role="list">
            {pagedOrders.length ? (
              pagedOrders.map((order) => {
                const id = String(order?.id || "");
                const shortId = id ? id.slice(0, 8) : "—";
                const pics = getOrderPictures(order);
                const uploadsCount = pics.length;
                const totalQty = sumQuantities(pics);
                const active = selectedId && selectedId === String(order.id);
                const status = String(order?.status || "Pending");

                return (
                  <button
                    key={id}
                    type="button"
                    className={`cp-orders__row ${active ? "is-active" : ""}`}
                    onClick={() => handleOpenDetails(order)}
                  >
                    <div className="cp-orders__rowTL">
                      <div className="cp-orders__orderId">#{shortId}</div>
                    </div>

                    <div className="cp-orders__rowTR">
                      <div
                        className={`cp-orders__statusCard cp-orders__status--${statusKey(
                          status
                        )}`}
                      >
                        {status}
                      </div>
                    </div>

                    <div className="cp-orders__rowBL">
                      <div className="cp-orders__rowBottom">
                        <span>{formatDateTime(order?.createdAt)}</span>
                        <span>•</span>
                        <span>
                          {uploadsCount} uploads
                          {totalQty && totalQty !== uploadsCount
                            ? ` (${totalQty} qty)`
                            : ""}
                        </span>
                      </div>
                    </div>

                    <div className="cp-orders__rowBR">
                      <div className="cp-orders__total">
                        {formatMoney(order?.subtotal)}
                      </div>
                      <div className="cp-orders__chev" aria-hidden="true">
                        ›
                      </div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div style={{ padding: 12 }}>
                <div className="cp-orders__empty">
                  No orders match your filters.
                </div>
              </div>
            )}
          </div>

          {filteredOrders.length > pageSize ? (
            <div
              className="cp-orders__pagination"
              role="navigation"
              aria-label="Orders pagination"
            >
              <div className="cp-orders__paginationLeft">
                <span className="cp-orders__paginationText">
                  Page {safePageIndex + 1} of {totalPages}
                </span>
                <span className="cp-orders__paginationText">
                  Showing{" "}
                  {Math.min(
                    filteredOrders.length,
                    safePageIndex * pageSize + 1
                  )}
                  –
                  {Math.min(
                    filteredOrders.length,
                    (safePageIndex + 1) * pageSize
                  )}{" "}
                  of {filteredOrders.length}
                </span>
              </div>

              <div className="cp-orders__paginationRight">
                <label className="cp-orders__paginationText">
                  Per page{" "}
                  <select
                    className="cp-orders__select cp-orders__select--sm"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </label>

                <button
                  type="button"
                  className="op-btn op-btn--ghost"
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                  disabled={safePageIndex <= 0}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="op-btn op-btn--ghost"
                  onClick={() =>
                    setPageIndex((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={safePageIndex >= totalPages - 1}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div
          className={`cp-orders__detail ${selectedOrder ? "is-open" : ""}`}
          aria-live="polite"
          role={selectedOrder ? "dialog" : undefined}
          aria-modal={selectedOrder ? "true" : undefined}
        >
          {selectedOrder ? (
            <>
              <div className="cp-orders__detailHeader">
                <button
                  type="button"
                  className="cp-orders__backBtn"
                  onClick={handleCloseDetails}
                >
                  <span className="cp-orders__backArrow" aria-hidden="true">
                    ←
                  </span>
                  BACK
                </button>
                <div className="cp-orders__detailHeaderCenter">
                  <div className="cp-orders__detailTitle">
                    ORDER {String(selectedOrder.id || "").slice(0, 8)}
                  </div>
                  <div className="cp-orders__detailSub">
                    {formatDateTime(selectedOrder?.createdAt)}
                  </div>
                </div>
                <div className="cp-orders__detailHeaderRight">
                  <span
                    className={`cp-orders__statusDetail cp-orders__status--${statusKey(selectedOrder?.status)}`}
                  >
                    {String(selectedOrder?.status || "Pending")}
                  </span>
                </div>
              </div>

              <div className="cp-orders__statusBlock">
                <div
                  className={`cp-orders__statusProgress ${isCancelled ? "is-cancelled" : ""}`}
                  role="img"
                  aria-label={`Order status ${selectedOrder?.status || "Pending"}`}
                  style={{
                    "--steps": renderStatusFlow.length,
                    "--progress": statusProgressRatio,
                  }}
                >
                  <div className="cp-orders__statusTrack" aria-hidden />
                  <div
                    className="cp-orders__statusTrack is-filled"
                    aria-hidden
                  />
                  {renderStatusFlow.map((step, index) => {
                    const isDone =
                      index < activeStatusIndex ||
                      (activeStatusIndex === renderStatusFlow.length - 1 &&
                        index === activeStatusIndex);
                    const current = index === activeStatusIndex;
                    return (
                      <div key={step.key} className="cp-orders__statusStep">
                        <div
                          className={`cp-orders__statusDot ${isDone ? "is-done" : ""} ${current ? "is-current" : ""}`}
                          aria-hidden="true"
                        />
                        <div className="cp-orders__statusStepLabel">
                          {step.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="cp-orders__detailBody">
                <div>
                  <div className="cp-orders__sectionTitle">Pictures</div>
                  <div className="cp-orders__canvasList">
                    {getOrderPictures(selectedOrder).length ? (
                      getOrderPictures(selectedOrder).map((pic, index) => {
                        const normalized = normalizePicture(pic, index);
                        const dims = normalized.url
                          ? imgDims[normalized.url]
                          : null;
                        const inches =
                          normalized.widthIn && normalized.heightIn
                            ? `${normalized.widthIn}" × ${normalized.heightIn}"`
                            : dims
                              ? `${(dims.width / 300).toFixed(2)}" × ${(dims.height / 300).toFixed(2)}"`
                              : "Measuring…";

                        return (
                          <button
                            key={`${normalized.url || "img"}:${index}`}
                            type="button"
                            className="cp-orders__canvas"
                            onClick={() => {
                              if (!normalized.url) return;
                              setPreviewUrl(normalized.url);
                              setPreviewTitle(normalized.name || "Preview");
                            }}
                            disabled={!normalized.url}
                            title={
                              normalized.url ? "Preview" : "No image available"
                            }
                          >
                            <img
                              className="cp-orders__thumb"
                              src={normalized.url || undefined}
                              alt={normalized.name || `Image ${index + 1}`}
                              loading="lazy"
                            />
                            <div style={{ minWidth: 0 }}>
                              <div className="cp-orders__canvasName">
                                {normalized.name}
                              </div>
                              <div className="cp-orders__canvasMeta">
                                <span>Size: {inches}</span>
                                <span>•</span>
                                <span>Qty: {normalized.quantity}</span>
                              </div>
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="cp-orders__empty">
                        No uploaded pictures found on this order.
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="cp-orders__sectionTitle">Pricing</div>
                  <div className="cp-orders__pricingCard">
                    {(() => {
                      const subtotal =
                        toMoneyNumber(selectedOrder?.subtotal) ?? 0;
                      const discount =
                        toMoneyNumber(selectedOrder?.discount) ?? 0;
                      const tax = toMoneyNumber(selectedOrder?.tax) ?? 0;
                      const total =
                        toMoneyNumber(selectedOrder?.total) ?? subtotal;
                      const amountPaid = toMoneyNumber(
                        selectedOrder?.amountPaid
                      );
                      const base = subtotal - discount + tax;
                      const fees = total - base;
                      const balanceDue =
                        amountPaid === null
                          ? null
                          : Math.max(0, total - amountPaid);

                      const showDiscount = Math.abs(discount) > 0.005;
                      const showTax = Math.abs(tax) > 0.005;
                      const showFees = Math.abs(fees) > 0.005;

                      return (
                        <>
                          <div className="cp-orders__pricingRow">
                            <span>Subtotal</span>
                            <span>{formatMoney(subtotal)}</span>
                          </div>
                          {showDiscount ? (
                            <div className="cp-orders__pricingRow">
                              <span>Discount</span>
                              <span>-{formatMoney(Math.abs(discount))}</span>
                            </div>
                          ) : null}
                          {showTax ? (
                            <div className="cp-orders__pricingRow">
                              <span>Tax</span>
                              <span>{formatMoney(tax)}</span>
                            </div>
                          ) : null}
                          {showFees ? (
                            <div className="cp-orders__pricingRow">
                              <span>{fees >= 0 ? "Fees" : "Adjustment"}</span>
                              <span>
                                {fees >= 0
                                  ? formatMoney(fees)
                                  : `-${formatMoney(Math.abs(fees))}`}
                              </span>
                            </div>
                          ) : null}
                          <div className="cp-orders__pricingRow is-total">
                            <span>Total</span>
                            <span>{formatMoney(total)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {selectedOrder?.invoice?.pdfUrl ? (
                  <button
                    type="button"
                    className="op-btn cp-cta--primary"
                    onClick={() =>
                      window.open(
                        selectedOrder.invoice.pdfUrl,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    View invoice PDF
                  </button>
                ) : null}
              </div>
            </>
          ) : (
            <div style={{ padding: 12 }}>
              <div className="cp-orders__empty">
                Select an order to see its uploaded pictures, totals, and
                invoice links.
              </div>
            </div>
          )}
        </div>
      </div>

      {previewUrl ? (
        <div
          className="cp-orders__previewOverlay"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => {
            setPreviewUrl("");
            setPreviewTitle("");
          }}
        >
          <div
            className="cp-orders__previewModal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-orders__previewHeader">
              <div className="cp-orders__previewTitle" title={previewTitle}>
                {previewTitle || "Preview"}
              </div>
              <button
                type="button"
                className="op-btn op-btn--ghost"
                onClick={() => {
                  setPreviewUrl("");
                  setPreviewTitle("");
                }}
              >
                Close
              </button>
            </div>
            <div className="cp-orders__previewBody">
              <img
                className="cp-orders__previewImg"
                src={previewUrl}
                alt={previewTitle || "Preview"}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
